// FAQ accordion parsing for the Help page (#4210).
//
// The FAQ sections are not separate documents: they are `<details>` /
// `<summary>` blocks inside the markdown of the `@help:help.index` document. So
// once that document is translated, the section headings are translated too.
//
// These helpers are deliberately free of Angular DI so they run under plain node
// in unit tests, the same split `text-locale.ts` uses. See
// design-docs/global-common-localization.md.

export interface FaqSection {
  /** The heading shown to the user, in the language the app is running in. */
  summary: string;
  details: string;
  /**
   * The ENGLISH heading, reported as the telemetry value for this section.
   *
   * The visible `summary` is translated, so reporting it would split one FAQ
   * section into a different value per language and make the numbers
   * uncomparable. Falls back to `summary` when the English headings cannot be
   * paired up — see `pairEnglishSummaries`.
   */
  englishSummary: string;
}

/** Extract the `<details>` / `<summary>` sections from a help document's markdown. */
export function parseFaqSections(markdown: string): FaqSection[] {
  const sections: FaqSection[] = [];
  // build the regex here rather than at module level: a `/g` regex carries a
  // lastIndex between uses, and a shared one would make this function's result
  // depend on who called it last
  const detailsRegex = /<details[^>]*>\s*<summary[^>]*>([\s\S]*?)<\/summary>([\s\S]*?)<\/details>/gi;

  let match: RegExpExecArray | null;
  while ((match = detailsRegex.exec(markdown || '')) !== null) {
    const summary = match[1].trim();
    const details = match[2].trim();
    // default the reported heading to the visible one; pairEnglishSummaries
    // replaces it with the English heading for a non-English reader
    sections.push({ summary, details, englishSummary: summary });
  }

  return sections;
}

/** What `pairEnglishSummaries` did, so the caller can report a structural mismatch. */
export const enum FaqPairingOutcome {
  /** Every section now carries its English heading. */
  Paired = 'paired',
  /** The two documents have different section counts, so nothing was changed. */
  CountMismatch = 'count-mismatch',
}

/**
 * Give each section its ENGLISH heading, pairing the two documents BY POSITION.
 *
 * Pairing by position is sound because a translation keeps the same `<details>`
 * blocks in the same order — the translator brief requires it and the structural
 * validator enforces it.
 *
 * If the counts disagree the pairing is abandoned rather than guessed at: the
 * visible headings stay as the reported value, which is wrong-but-harmless
 * instead of confidently mislabelled. The caller reports that case, because it
 * means a translated `help.index` has drifted structurally from the English one.
 *
 * Mutates `localizedSections` in place, so a template already bound to those
 * objects keeps working.
 */
export function pairEnglishSummaries(
  localizedSections: FaqSection[],
  englishSections: readonly FaqSection[],
): FaqPairingOutcome {
  if (localizedSections.length !== englishSections.length) {
    return FaqPairingOutcome.CountMismatch;
  }
  for (let i = 0; i < localizedSections.length; i++) {
    localizedSections[i].englishSummary = englishSections[i].summary;
  }
  return FaqPairingOutcome.Paired;
}
